import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import api from "@/lib/api";

const Shell = ({ children, testid }) => (
  <div className="App flex min-h-screen items-center justify-center px-5" data-testid={testid}>
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 text-center sm:p-10">
      <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Payments</div>
      {children}
      <Link to="/portal" className="mt-8 inline-block bg-amber-600 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500" data-testid="payment-back-portal">
        Back to my portal
      </Link>
    </motion.div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0, amount: null });

  useEffect(() => {
    if (!sessionId) return setState({ status: "error" });
    let cancelled = false;
    const poll = async (attempt) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState({ status: "paid", amount: data.amount });
        if (["failed", "expired"].includes(data.payment_status)) return setState({ status: "failed" });
      } catch {
        if (cancelled) return;
      }
      if (attempt >= 8) return setState({ status: "timeout" });
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Shell testid="payment-success-page">
      {state.status === "checking" && (
        <div className="mt-10" data-testid="payment-checking">
          <Loader2 size={32} className="mx-auto animate-spin text-amber-500" />
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Confirming your payment…</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="mt-10" data-testid="payment-paid">
          <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
          <h1 className="mt-5 text-2xl font-bold text-slate-50">Payment received</h1>
          <p className="mt-3 text-sm text-slate-400">Thank you — NZ${state.amount?.toLocaleString()} has been paid. Your service record is now marked as paid and a receipt has been emailed by Stripe.</p>
        </div>
      )}
      {state.status === "timeout" && (
        <div className="mt-10" data-testid="payment-timeout">
          <Clock size={40} className="mx-auto text-amber-500" />
          <h1 className="mt-5 text-2xl font-bold text-slate-50">Still processing</h1>
          <p className="mt-3 text-sm text-slate-400">Your payment is taking a moment to confirm. Check your portal shortly — no need to pay again.</p>
        </div>
      )}
      {(state.status === "failed" || state.status === "error") && (
        <div className="mt-10" data-testid="payment-failed">
          <XCircle size={40} className="mx-auto text-red-400" />
          <h1 className="mt-5 text-2xl font-bold text-slate-50">Payment not completed</h1>
          <p className="mt-3 text-sm text-slate-400">We couldn't confirm this payment. Nothing has been charged — you can try again from your portal.</p>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel-page">
      <div className="mt-10" data-testid="payment-cancelled">
        <XCircle size={40} className="mx-auto text-slate-500" />
        <h1 className="mt-5 text-2xl font-bold text-slate-50">Payment cancelled</h1>
        <p className="mt-3 text-sm text-slate-400">No charge was made. Your invoice is still waiting in the portal whenever you're ready.</p>
      </div>
    </Shell>
  );
}
